if (!customElements.get('timeline-slider')) {
  class TimelineSlider extends HTMLElement {
    static selectors = {
      arrows: {
        left: '.arrow-left',
        right: '.arrow-right',
      },
      contentSlider: '[js-content-slider]',
      imageSlider: '[js-image-slider]',
    };

    static SWIPER_ACTIVE_CLASS = 'swiper-currently-active';
    static SPACE_BETWEEN_DESKTOP = 41;
    static SPACE_BETWEEN_MOBILE = 32;

    constructor() {
      super();

      this.mediaQuery = window.matchMedia('(max-width: 749px)');
      this.autoSlideInterval = null;
    }

    /**
     * @returns {Number}
     */
    calculateSliderOffset() {
      // 67 is half the slide width
      const offset = window.innerWidth / 2 - 67;

      return this.mediaQuery.matches ? offset : 0;
    }

    /**
     * Function generated by GPT-4
     * @param {Swiper} activeSwiper
     * @param {Swiper} syncedSwiper
     * @returns
     */
    onSlideChange(activeSwiper, syncedSwiper) {
      if (!activeSwiper || !syncedSwiper) return;

      // Ensure to only synchronize if the active swiper's slide is different from the synced swiper's slide
      if (activeSwiper.activeIndex !== syncedSwiper.activeIndex) {
        syncedSwiper.slideTo(activeSwiper.activeIndex);
      }

      // Update the active class
      activeSwiper.slides.forEach((slide, index) => {
        slide.classList.toggle(
          TimelineSlider.SWIPER_ACTIVE_CLASS,
          index === activeSwiper.activeIndex,
        );
      });

      syncedSwiper.slides.forEach((slide, index) => {
        slide.classList.toggle(
          TimelineSlider.SWIPER_ACTIVE_CLASS,
          index === syncedSwiper.activeIndex,
        );
      });
    }

    initImageSlider() {
      if (!this.imageSliderElement) return;

      const offset = this.calculateSliderOffset();

      this.imageSliderSettings = {
        slidesPerView: 'auto',
        spaceBetween: TimelineSlider.SPACE_BETWEEN_DESKTOP,
        centerInsufficientSlides: true,
        allowTouchMove: false,
        slidesOffsetBefore: offset,
        slidesOffsetAfter: offset,
        breakpoints: {
          750: {
            spaceBetween: TimelineSlider.SPACE_BETWEEN_MOBILE,
            slidesOffsetBefore: 0,
          },
        },
        on: {
          resize: (swiper) => {
            const offset = this.calculateSliderOffset();
            swiper.slidesOffsetBefore = offset;
            swiper.slidesOffsetAfter = offset;
          },
          slideChange: () =>
            this.onSlideChange(
              this.imageSliderInstance,
              this.contentSliderInstance,
            ),
        },
      };

      this.imageSliderInstance = new Swiper(
        this.imageSliderElement,
        this.imageSliderSettings,
      );
    }

    initContentSlider() {
      if (!this.contentSliderElement) return;

      const offset = this.calculateSliderOffset();

      this.contentSliderSettings = {
        slidesPerView: 'auto',
        spaceBetween: TimelineSlider.SPACE_BETWEEN_DESKTOP,
        centerInsufficientSlides: true,
        allowTouchMove: false,
        slidesOffsetBefore: offset,
        slidesOffsetAfter: offset,
        breakpoints: {
          750: {
            slidesOffsetBefore: 0,
            spaceBetween: TimelineSlider.SPACE_BETWEEN_MOBILE,
          },
        },
        on: {
          resize: (swiper) => {
            const offset = this.calculateSliderOffset();
            swiper.slidesOffsetBefore = offset;
            swiper.slidesOffsetAfter = offset;
          },
          slideChange: () =>
            this.onSlideChange(
              this.imageSliderInstance,
              this.contentSliderInstance,
            ),
        },
      };

      if (this.imageSliderInstance) {
        this.contentSliderSettings.thumbs = {
          swiper: this.imageSliderInstance,
        };
      }

      this.contentSliderInstance = new Swiper(
        this.contentSliderElement,
        this.contentSliderSettings,
      );
    }

    /**
     * @param {Number} direction - 1 for Right / Next and -1 for Left / Previous
     */
    changeSlide(direction) {
      const totalSlides = this.imageSliderInstance.slides.length;

      this.currentIndex =
        (this.currentIndex + direction + totalSlides) % totalSlides;

      const prevSlideIndex =
        (this.currentIndex - direction + totalSlides) % totalSlides;

      this.imageSliderInstance.slides.forEach((slide, index) => {
        slide.classList.remove(
          TimelineSlider.SWIPER_ACTIVE_CLASS,
          index != this.currentIndex,
        );
      });

      this.imageSliderInstance.slideTo(this.currentIndex);

      const prevSlide = this.imageSliderInstance.slides[prevSlideIndex];
      if (prevSlide) {
        prevSlide.classList.remove(TimelineSlider.SWIPER_ACTIVE_CLASS);
      }

      const newSlide = this.imageSliderInstance.slides[this.currentIndex];
      if (!newSlide.classList.contains(TimelineSlider.SWIPER_ACTIVE_CLASS)) {
        newSlide.classList.add(TimelineSlider.SWIPER_ACTIVE_CLASS);
      }

      this.resetAutoSlideInterval();
    }

    clearAutoSlideInterval() {
      if (this.autoSlideInterval) clearInterval(this.autoSlideInterval);
    }

    resetAutoSlideInterval() {
      this.clearAutoSlideInterval();
      this.autoSlideInterval = setInterval(() => this.changeSlide(1), 3000);
    }

    disconnectedCallback() {
      this.clearAutoSlideInterval();
    }

    connectedCallback() {
      this.imageSliderElement = this.querySelector(
        TimelineSlider.selectors.imageSlider,
      );

      this.contentSliderElement = this.querySelector(
        TimelineSlider.selectors.contentSlider,
      );

      this.initImageSlider();
      this.initContentSlider();

      if (!this.imageSliderInstance) return;

      this.currentIndex = this.imageSliderInstance.activeIndex;

      this.imageSliderInstance.slides[this.currentIndex].classList.add(
        TimelineSlider.SWIPER_ACTIVE_CLASS,
      );

      this.autoSlideInterval = setInterval(() => {
        this.changeSlide(1);
      }, 3000);

      const prevEl = this.querySelector(TimelineSlider.selectors.arrows.left);
      const nextEl = this.querySelector(TimelineSlider.selectors.arrows.right);

      if (prevEl) {
        prevEl.addEventListener('click', () => {
          if (this.currentIndex === 0) {
            return;
          }

          this.changeSlide(-1);
        });
      }

      if (nextEl) {
        nextEl.addEventListener('click', () => {
          if (
            this.currentIndex ===
            this.imageSliderInstance.slides.length - 1
          ) {
            return;
          }

          this.changeSlide(1);
        });
      }
    }
  }

  customElements.define('timeline-slider', TimelineSlider);
}
